import { ReactNode } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FormattedMessage } from "react-intl";
import { ApplicantDocument } from "../types/applicants";

interface ApplicantDocumentUploadProps {
  label: ReactNode;
  helperText?: ReactNode;
  currentDocument?: ApplicantDocument;
  selectedFile?: File | null;
  errorText?: string;
  disabled?: boolean;
  onFileChange: (file: File | undefined) => void;
  onOpenCurrentDocument?: () => void | Promise<void>;
  onRemoveCurrentDocument?: () => void | Promise<void>;
}

export const ApplicantDocumentUpload = ({
  label,
  helperText,
  currentDocument,
  selectedFile,
  errorText,
  disabled = false,
  onFileChange,
  onOpenCurrentDocument,
  onRemoveCurrentDocument,
}: ApplicantDocumentUploadProps) => {
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, marginBottom: "0.5rem" }}>{label}</Typography>
      {helperText && (
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "0.75rem" }}>
          {helperText}
        </Typography>
      )}
      <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={disabled}>
        <FormattedMessage
          id="document_upload_choose_image"
          defaultMessage="Choose image"
        />
        <input
          type="file"
          hidden
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          onChange={(event) => {
            onFileChange(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </Button>
      {selectedFile && (
        <Typography variant="body2" sx={{ marginTop: "0.75rem" }}>
          <FormattedMessage
            id="document_upload_selected_file"
            defaultMessage="Selected file: {fileName}"
            values={{ fileName: selectedFile.name }}
          />
        </Typography>
      )}
      {currentDocument && (currentDocument.url || currentDocument.storagePath) && (
        <Box sx={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2">
            <FormattedMessage
              id="document_upload_current_file"
              defaultMessage="Current file:"
            />{" "}
            <Link
              href={currentDocument.url || "#"}
              target={currentDocument.url ? "_blank" : undefined}
              rel={currentDocument.url ? "noreferrer" : undefined}
              onClick={(event) => {
                if (!currentDocument.url && onOpenCurrentDocument) {
                  event.preventDefault();
                  void onOpenCurrentDocument();
                }
              }}
            >
              <FormattedMessage
                id="document_open_image"
                defaultMessage="Open image"
              />
            </Link>
          </Typography>
          {onRemoveCurrentDocument && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                void onRemoveCurrentDocument();
              }}
              disabled={disabled}
            >
              <FormattedMessage
                id="document_remove_image"
                defaultMessage="Remove image"
              />
            </Button>
          )}
        </Box>
      )}
      {errorText && (
        <Typography variant="body2" color="error" sx={{ marginTop: "0.75rem" }}>
          {errorText}
        </Typography>
      )}
    </Box>
  );
};
